import { useConsentManager } from "@c15t/react";
import { faQuestionCircle } from "@fortawesome/pro-regular-svg-icons";

import Icon from "common/components/Icon";

import Button from "client/common/components/buttons/Button";
import CloseButton from "client/common/components/buttons/CloseButton";
import TextButton from "client/common/components/buttons/TextButton";
import Tooltip from "client/common/components/content/Tooltip";
import Switch from "client/common/components/inputs/Switch";
import { INITIAL_CLIENT_DATA } from "client/common/utils";

import { useConsentBannerMutation } from "../hooks";
import { externalUrls } from "../hooks/constants";
import ConsentDialog from "./ConsentDialog";

import styles from "../styles/ConsentPreferencesDialog.module.less";

interface ConsentPreferencesDialogProps {
  onClose: () => void;
}

export const ConsentPreferencesDialog = ({ onClose }: ConsentPreferencesDialogProps) => {
  const { consents, selectedConsents, setSelectedConsent, saveConsents, hasConsented } =
    useConsentManager();
  const { mutate: syncBannerConsent } = useConsentBannerMutation();

  const typeformUrl = `${externalUrls.typeformDataRequest}?utm_source=clinic_id_${INITIAL_CLIENT_DATA.clinic.id}`;

  const handleCancel = () => {
    if (hasConsented()) {
      setSelectedConsent("experience", consents.experience);
      setSelectedConsent("functionality", consents.functionality);
      setSelectedConsent("marketing", consents.marketing);
      setSelectedConsent("measurement", consents.measurement);
    }
    onClose();
  };

  const handleSave = () => {
    setSelectedConsent("necessary", true);
    saveConsents("custom");

    syncBannerConsent({
      experience: selectedConsents.experience,
      functionality: selectedConsents.functionality,
      marketing: selectedConsents.marketing,
    });

    onClose();
  };

  return (
    <ConsentDialog onClose={handleCancel}>
      <div className={styles.dialog}>
        <div className={styles.header}>
          <h2 className={styles.title}>Your privacy choices</h2>
          <CloseButton onClick={handleCancel} className={styles.closeButton} aria-label="Close" />
        </div>

        <div className={styles.content}>
          <p className={styles.intro}>
            We take your privacy seriously and can provide you with the following options:
          </p>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Your privacy rights</h3>
            <ul className={styles.rightsList}>
              <li>
                <strong>
                  Right to opt-out of sharing of personal information / opt-out of targeted
                  advertising
                </strong>
                <br />
                You have the right to opt-out of sharing of personal information or opt-out of
                targeted advertising.
              </li>
              <li>
                <strong>Limit use of sensitive personal data</strong>
                <br />
                Sensitive personal information helps us show you offers, ads and content you may be
                interested in. If you limit the use, we&apos;ll only use your sensitive personal
                information as needed by law.
              </li>
              <li>
                <strong>Right to know</strong>
                <br />
                You have the right to request a report that details the categories of personal
                information collected about you, the categories of sources from which the personal
                information is collected, the purposes for collecting the personal information, the
                categories of third parties with whom we share the personal information, and the
                specific pieces of personal information collected about you over the past 12 months.
              </li>
              <li>
                <strong>Right to delete</strong>
                <br />
                You have the right to request that we delete certain personal information we
                collected from you.
              </li>
              <li>
                <strong>Right to correct</strong>
                <br />
                You have the right to correct personal information we hold about you.
              </li>
            </ul>
          </section>

          <section className={styles.section}>
            <div className={styles.toggleSection}>
              <div className={styles.toggleContent}>
                <h3 className={styles.sectionTitle}>
                  Do not sell or share my personal information
                </h3>
                <p className={styles.sectionDescription}>
                  We don&apos;t sell your personal information for monetary compensation. Sharing
                  your personal information with our partners allows us to show you offers, ads or
                  content that are uniquely relevant to you. This activity may be considered a sale,
                  sharing for cross-context behavioral advertising or targeted advertising.
                </p>
                <p className={styles.sectionDescription}>
                  To opt out, please move the toggle to the right.
                </p>
                <p className={styles.sectionDescription}>
                  Unless you are logged into your account, our opt-out will be specific to the
                  browser and/or device you are using to make the request. Note that if you opt out,
                  you may still see ads, although they may not be personalized to you. If you clear
                  your cookies, or use a new browser or device to access the site, please submit a
                  new request to re-apply your opt-out choice.
                </p>
                <p className={styles.sectionDescription}>
                  Note that you also can use the Global Privacy Control (&quot;GPC&quot;) to signal
                  your opt-out preference. To learn more about the GPC please visit{" "}
                  <TextButton href={externalUrls.gpc} hasUnderline={true}>
                    {externalUrls.gpc}
                  </TextButton>
                  . You can update your browser settings at any time to restrict or disable certain
                  cookies. If you delete cookies, reset your device, or switch your experience
                  between web and mobile, you may need to set your preference again to ensure we
                  continue to honor your preferred choice.
                </p>
              </div>
              <Switch
                value={selectedConsents.experience === false}
                onChange={(value) => setSelectedConsent("experience", !value)}
              />
            </div>
          </section>

          <section className={styles.section}>
            <div className={styles.toggleSection}>
              <div className={styles.toggleContent}>
                <h3 className={styles.sectionTitle}>
                  Limit the use of my sensitive personal information
                </h3>
                <p className={styles.sectionDescription}>
                  Sensitive personal information may help us show you offers, ads, and content you
                  might be interested in. If you limit the use, we&apos;ll only use your sensitive
                  personal information as needed by law.
                </p>
                <p className={styles.sectionDescription}>
                  To opt out, please move the toggle to the right.
                </p>
                <p className={styles.sectionDescription}>
                  We do not today use your sensitive personal information but you can save this
                  setting for the future.
                </p>
              </div>
              <Switch
                value={selectedConsents.functionality === false}
                onChange={(value) => setSelectedConsent("functionality", !value)}
              />
            </div>
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              Right to know/access, correct, and delete personal information
            </h3>
            <div className={styles.rightsList}>
              <p className={styles.sectionDescription}>
                <strong>Right to know/access and correct personal information:</strong>
                <br />
                If you have an account, you can access and correct the personal information
                affiliated with your account by logging in and going to “My Account”.
                <br />
                Regardless of whether you have an Account or not, you can request a report of the
                personal information that we may have about you and request any such personal
                information be corrected.
              </p>
              <p className={styles.sectionDescription}>
                <TextButton href={typeformUrl} hasUnderline={true}>
                  <strong>Request a report/correction</strong>
                </TextButton>
              </p>
              <p className={styles.sectionDescription}>
                <strong>Right to delete</strong>
                <br />
                Select the link below to request the deletion of your account and/or any personal
                information we may have about you.
              </p>
              <p className={styles.sectionDescription}>
                Please note, if you choose to delete your Account or personal information, any
                active subscriptions, autoship orders, and ongoing promotions or deals will be
                canceled. You may also lose access to order history and saved preferences. This
                action is permanent and cannot be undone.
              </p>
              <p className={styles.sectionDescription}>
                <TextButton href={typeformUrl} hasUnderline={true}>
                  <strong>Request to delete</strong>
                </TextButton>
              </p>
            </div>
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Cookie settings</h3>
            <div className={styles.toggleSection}>
              <div className={styles.toggleContent}>
                <Tooltip
                  isEnabled={true}
                  position="above"
                  className={styles.tooltip}
                  content={
                    <p>
                      These cookies are necessary for the website to function and cannot be switched
                      off in our systems. They are usually only set in response to actions made by
                      you which amount to a request for services, such as setting your privacy
                      preferences, logging in or filling in forms. Because these cookies are
                      essential to provide the services you request, they are always active and do
                      not require your consent under applicable U.S. privacy laws. These cookies do
                      not store any personally identifiable information.
                    </p>
                  }
                >
                  <h3 className={styles.sectionTitle}>
                    Strictly necessary cookies&nbsp;
                    <Icon icon={faQuestionCircle} className={styles.infoIcon} />
                  </h3>
                  <p className={styles.cookieDescription}>
                    These cookies are essential for the website to function and cannot be disabled.
                  </p>
                </Tooltip>
              </div>
              <Switch value={true} onChange={() => {}} disabled={true} />
            </div>
            <div className={styles.toggleSection}>
              <div className={styles.toggleContent}>
                <h3 className={styles.sectionTitle}>Accept all cookies</h3>
                <p className={styles.cookieDescription}>
                  Includes marketing and analytics cookies for personalized content and site
                  improvement.
                </p>
              </div>
              <Switch
                value={selectedConsents.marketing === true}
                onChange={(value) => {
                  setSelectedConsent("marketing", value);
                  setSelectedConsent("measurement", value);
                }}
              />
            </div>
          </section>

          <div className={styles.actions}>
            <Button variant="secondary" size="s" onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="primary" size="s" onClick={handleSave}>
              Save preferences
            </Button>
          </div>
        </div>
      </div>
    </ConsentDialog>
  );
};
